// The exposé gallery's lightbox.
//
// Progressive enhancement over the markup in
// `hp_real_estate/partials/_offer-main.html`: every tile there is already an
// <a> pointing at that picture's `gallery_full` rendition, so a click does
// something useful before this file runs. What the lightbox adds is the
// overlay — and with it the only route to pictures six and up, which the
// four-thumb strip has no room for and which the count badge nonetheless
// promises.
//
// Native <dialog>.showModal() carries the modal behaviour we would otherwise
// hand-roll: Escape to close, a focus trap, focus restored to the tile that
// opened it, and a styleable ::backdrop. Only the arrow keys and the swipe are
// ours — the scroll lock is CSS, keyed off the dialog's [open] in
// components/_lightbox.scss, so nothing here has to track whether it is up.

import { push } from "./tracking.js";

const GALLERY_SELECTOR = "[data-re-gallery]";
const ITEM_SELECTOR = "[data-re-gallery-item]";

// Under this a horizontal drag is a scroll, not a swipe.
const SWIPE_THRESHOLD_PX = 40;

// Built on first open, then reused: an exposé has one gallery, and a dialog
// that is never opened has no business in the DOM.
let lightbox = null;

const state = { items: [], index: 0 };

// Wraps, so the arrows never dead-end and the last picture leads back to the
// first.
function wrap(index, count) {
    return ((index % count) + count) % count;
}

function show(index) {
    const count = state.items.length;
    state.index = wrap(index, count);

    const item = state.items[state.index];
    lightbox.image.src = item.src;
    lightbox.image.alt = item.alt;
    lightbox.caption.textContent = item.caption;
    lightbox.caption.hidden = !item.caption;
    lightbox.counter.textContent = `${state.index + 1} von ${count}`;

    // One picture needs neither arrows nor a counter, and a gallery of one is
    // a real case — plenty of offers arrive with a single photograph.
    const single = count < 2;
    lightbox.prev.hidden = single;
    lightbox.next.hidden = single;
    lightbox.counter.hidden = single;

    preload(state.index + 1);
    preload(state.index - 1);
}

function step(direction) {
    show(state.index + direction);
}

// The neighbours, so an arrow press shows a picture rather than an empty
// stage. `gallery_full` is up to 1400px wide and these are not cheap.
function preload(index) {
    const count = state.items.length;
    if (count < 2) {
        return;
    }
    new Image().src = state.items[wrap(index, count)].src;
}

function buildLightbox() {
    const dialog = document.createElement("dialog");
    dialog.className = "hp_re-lightbox";
    // A static string. Nothing from the offer goes in here — the caption and
    // the alt text are assigned through textContent and .alt in show().
    dialog.innerHTML = `
        <div class="hp_re-lightbox-stage">
            <figure class="hp_re-lightbox-figure">
                <img class="hp_re-lightbox-image" alt="">
                <figcaption class="hp_re-lightbox-caption"></figcaption>
            </figure>
        </div>
        <p class="hp_re-lightbox-counter" role="status"></p>
        <button type="button" class="hp_re-lightbox-close"><span class="visually-hidden">Schließen</span></button>
        <button type="button" class="hp_re-lightbox-nav hp_re-lightbox-prev"><span class="visually-hidden">Vorheriges Bild</span></button>
        <button type="button" class="hp_re-lightbox-nav hp_re-lightbox-next"><span class="visually-hidden">Nächstes Bild</span></button>
    `;

    const parts = {
        dialog,
        stage: dialog.querySelector(".hp_re-lightbox-stage"),
        image: dialog.querySelector(".hp_re-lightbox-image"),
        caption: dialog.querySelector(".hp_re-lightbox-caption"),
        counter: dialog.querySelector(".hp_re-lightbox-counter"),
        prev: dialog.querySelector(".hp_re-lightbox-prev"),
        next: dialog.querySelector(".hp_re-lightbox-next"),
    };

    dialog.querySelector(".hp_re-lightbox-close").addEventListener("click", () => dialog.close());
    parts.prev.addEventListener("click", () => step(-1));
    parts.next.addEventListener("click", () => step(1));

    // The stage is the space around the picture, so a click on it is a click
    // "outside" — the dismiss gesture that would normally land on ::backdrop,
    // which never sees a pointer event while the stage covers the viewport.
    dialog.addEventListener("click", (event) => {
        if (event.target === parts.stage || event.target === dialog) {
            dialog.close();
        }
    });

    dialog.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            step(-1);
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            step(1);
        }
    });

    let touchStartX = null;
    parts.stage.addEventListener(
        "touchstart",
        (event) => {
            touchStartX = event.touches.length === 1 ? event.touches[0].clientX : null;
        },
        { passive: true },
    );
    parts.stage.addEventListener(
        "touchend",
        (event) => {
            if (touchStartX === null) {
                return;
            }
            const travelled = event.changedTouches[0].clientX - touchStartX;
            touchStartX = null;
            if (Math.abs(travelled) >= SWIPE_THRESHOLD_PX) {
                step(travelled < 0 ? 1 : -1);
            }
        },
        { passive: true },
    );

    document.body.appendChild(dialog);
    return parts;
}

function open(items, index, uid) {
    if (!lightbox) {
        lightbox = buildLightbox();
    }
    state.items = items;
    show(index);
    lightbox.dialog.showModal();
    // Reported from here rather than from a `data-re-track` on the tiles: the
    // tiles are real links, and a tile click that keeps its default (a
    // middle-click, a ctrl-click) opens the picture in a new tab without ever
    // raising the lightbox. This fires when the dialog actually goes up.
    push("realEstateGalleryOpen", {
        offer_uid: uid,
        // 1-based, because it is read next to the "1 / 9" the lightbox prints.
        image_index: index + 1,
        image_count: items.length,
    });
}

// Hidden tiles are collected too: they are how the lightbox reaches the
// pictures the strip has no room for.
function collect(gallery) {
    return Array.from(gallery.querySelectorAll(ITEM_SELECTOR)).map((tile) => ({
        tile,
        src: tile.href,
        alt: tile.dataset.reGalleryAlt || "",
        caption: tile.dataset.reGalleryCaption || "",
    }));
}

function init() {
    document.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
        const items = collect(gallery);
        if (!items.length) {
            return;
        }
        const uid = gallery.dataset.reGalleryUid || "";

        // Only now may the tiles look clickable. Without this file they are
        // plain links to an image file, and a zoom cursor would oversell that.
        gallery.classList.add("hp_re-gallery-enhanced");

        items.forEach((item, index) => {
            item.tile.addEventListener("click", (event) => {
                // The tiles are real links, so leave the gestures that mean
                // "open it separately" alone.
                if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }
                event.preventDefault();
                open(items, index, uid);
            });
        });
    });
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
